import Alert, { AlertColor } from '@mui/material/Alert';

import HtmlMessage from './HtmlMessage';

type Props = {
  severity: AlertColor;
  html: string;
};

/**
 * Full-bleed notice across the top of a page, with its message centred. Shared
 * by the login page's `bannerMessage` and the signed-in app's `inAppBanner` so
 * the two read as the same component.
 */
const TopBanner = ({ severity, html }: Props) => (
  // Square corners because it meets both edges of the viewport.
  // `pre-line` honours newlines in the flag text while still wrapping
  // long lines; without it a multi-paragraph message collapses into one.
  <Alert
    severity={severity}
    sx={{
      borderRadius: 0,
      whiteSpace: 'pre-line',
      // One step up the scale from the `body2` (12px) MUI gives an Alert,
      // which is undersized for a bar this wide. Taking the whole variant
      // rather than just its fontSize carries the matching 20px leading —
      // body2's 16px would crowd a multi-paragraph message.
      typography: 'body1',
      // Centre the icon against the text block rather than letting the
      // row stretch, which is what reads as "centred" on a full-bleed bar.
      alignItems: 'center',
      '& .MuiAlert-message': {
        // MUI ships this slot without flex-grow, so it has to be told to
        // fill the row before centring inside it means anything.
        flex: 1,
        textAlign: 'center',
        // Balances the icon, which is a sibling of the text rather than
        // part of it (22px glyph + 12px margin). Without this the centred
        // text settles ~17px right of the viewport's true centre.
        pr: '34px',
      },
    }}
  >
    <HtmlMessage html={html} />
  </Alert>
);

export default TopBanner;
